import { Client } from '@/hooks/useClients'

// Shared mapping between intake form submissions (intake_submissions rows,
// written by the intake-submit edge function) and the dashboard's client
// shapes. Used by ClientIntakeOnboarding (apply-to-existing-client) and the
// /clients/new wizard prefill (create-client-from-intake).

export interface IntakeSubmission {
  id: string
  company_name: string | null
  website: string | null
  contact_email: string | null
  research_state: string | null
  answers: Record<string, unknown>
  linked_client_id: string | null
  applied_at: string | null
  created_at: string
}

export const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null

export const normalizeDomain = (v: string | null | undefined): string | null => {
  if (!v) return null
  try {
    const host = v.includes('://') ? new URL(v).hostname : v.split('/')[0]
    return host.replace(/^www\./i, '').toLowerCase() || null
  } catch {
    return null
  }
}

// The form nests answers under `answers` or sends them flat (intake-submit
// stores the raw payload verbatim) — tolerate both.
export const answersOf = (s: IntakeSubmission): Record<string, unknown> => {
  const a = s.answers as Record<string, unknown>
  const nested = a?.answers
  return (nested && typeof nested === 'object' && !Array.isArray(nested))
    ? nested as Record<string, unknown>
    : (a || {})
}

export const crmValueOf = (a: Record<string, unknown>): string | null =>
  str(a.crm) === 'Other' && str(a.crmOther)
    ? `Other: ${str(a.crmOther)}`
    : str(a.crm)

// Everything that has no natural column is preserved as a readable block
// (appended to onboarding_notes), so nothing the client typed is lost.
export function buildNotesBlock(s: IntakeSubmission, a: Record<string, unknown>): string {
  const lines: string[] = [`--- Intake form submission ${new Date(s.created_at).toISOString().slice(0, 10)} ---`]
  const add = (label: string, v: unknown) => {
    const val = typeof v === 'object' && v != null ? JSON.stringify(v) : str(v)
    if (val) lines.push(`${label}: ${val}`)
  }

  if (str(a.researchState) === 'done') {
    add('ICP research result', a.researchResult)
    add('ICP match (client verdict)', a.icpMatch)
    add('ICP corrections', a.icpChanges)
  } else {
    add('ICP (client-written)', a.icp)
    add('Competitors', a.competitors)
  }
  add('Prior outbound', a.priorOutbound)
  add('Where prior data lives', a.priorLearnings)
  add('Success metric', a.successMetric)
  add('Target accounts', a.targetAccounts)
  add('Sending state', a.sendingState)
  add('Sending setup (domains/mailboxes managed at)', a.sendingSetup)
  add('Other tools', a.otherTools)
  add('Blocklist', a.blocklist)
  add('Accounts to avoid', a.avoidAccounts)
  add('Accounts to avoid (file/link)', a.avoidAccountsFile)
  add('Other stakeholders', a.otherStakeholders)
  add('Decision maker', a.approver)
  add('Hard deadlines (events)', a.hardDeadlines)
  add('Extra notes', a.extraNotes)
  add('Referral source', a.referralSource)
  add('Referred by', a.referralWho)

  return lines.length > 1 ? lines.join('\n') : ''
}

// Pseudo-Client for ClientWizard's `intakePrefill` prop: carries mapped
// values in the same DB-column shape the wizard's form initializer reads
// (columns + the JSON `notes` side-channel for fields without columns).
// Values only — it has NO id, and must never be passed as `client`.
export function buildPrefillClient(s: IntakeSubmission): Client {
  const a = answersOf(s)
  const notesJson: Record<string, unknown> = {}
  if (str(a.contactName)) notesJson.main_contact_name = str(a.contactName)
  if (str(a.hasRevOps) === 'yes' && str(a.revOpsName)) {
    notesJson.secondary_contact_name = str(a.revOpsName)
  }

  return {
    name: str(a.companyName) || s.company_name || '',
    company: str(a.companyName) || s.company_name || undefined,
    website: str(a.website) || s.website || undefined,
    main_contact_email: str(a.contactEmail) || s.contact_email || undefined,
    phone: str(a.contactPhone) || undefined,
    secondary_contact_email: str(a.hasRevOps) === 'yes' ? (str(a.revOpsEmail) || undefined) : undefined,
    crm_system: crmValueOf(a) || undefined,
    product_description: str(a.offering) || undefined,
    onboarding_notes: buildNotesBlock(s, a) || undefined,
    notes: Object.keys(notesJson).length > 0 ? JSON.stringify(notesJson) : undefined,
  } as unknown as Client
}
